/**
 * หน้าโพสต์ live updates (owner 23 ต.ค.: "ทั้งหน้าอัพเดทอัตโนมัติเวลาเปลี่ยนอะไรเปลี่ยนแปลง เช่น AI คิดเสร็จ").
 * The server (api/app/social/live.py, 8d368dd) sends `{"type": "social.changed", "kind": "posts"|"settings"|"ai"|
 * "publish", "post_ids": [int]}` once per transaction, to the owner's sockets only, on the app's one WebSocket
 * (api/realtime.ts → stores/inbox.ts, which owns the socket for the whole signed-in session) — ids and kinds only,
 * never a caption or a name. `post_ids` are numbers (Post.id is a string here: normalised below); [] = no post of its
 * own; left out (over 300) = any post may have changed. Events are not replayed after a dropped connection: the
 * reconnect arrives as `resync` (stores/inbox resync) and /posts reads everything again. Publishing one post sends ~6
 * events within a second — lib/postsLive turns a burst into one read.
 * Mock mode: MockPostsApi "sends" its own changes on the mock socket channel below, which api/realtime connectMock
 * forwards exactly like a real socket message — so the dev UI shows the same path (AI finishing a post, …).
 * Small on purpose: api/realtime imports it, so it stays in the main bundle without the posts page.
 */

export type SocialChangeKind = 'posts' | 'settings' | 'ai' | 'publish'
export interface SocialChangedEvent {
  type: 'social.changed'
  kind: SocialChangeKind
  /** the posts that changed (strings, like Post.id); null = not said (read everything of that kind again) */
  post_ids: string[] | null
}
/** what /posts hears: a change, or "events may have been missed — read everything again" */
export type SocialSignal = SocialChangedEvent | { type: 'resync' }

const KINDS: ReadonlySet<string> = new Set(['posts', 'settings', 'ai', 'publish'])

/** A socket message → the event, or null. Takes the flat shape (like every other event on this socket) and, to be
 *  safe, `{type, payload: {kind, post_ids}}`; ids may be numbers or strings. An unknown kind reads as `posts`. */
export function toSocialChangedEvent(v: unknown): SocialChangedEvent | null {
  if (typeof v !== 'object' || v === null || (v as { type?: unknown }).type !== 'social.changed') return null
  const raw = v as { kind?: unknown; post_ids?: unknown; payload?: unknown }
  const body = (typeof raw.payload === 'object' && raw.payload !== null ? raw.payload : raw) as { kind?: unknown; post_ids?: unknown }
  const kind = typeof body.kind === 'string' && KINDS.has(body.kind) ? (body.kind as SocialChangeKind) : 'posts'
  const ids = Array.isArray(body.post_ids)
    ? body.post_ids.filter((x) => typeof x === 'number' || (typeof x === 'string' && x !== '')).map(String)
    : null
  return { type: 'social.changed', kind, post_ids: ids }
}

type Listener<T> = (e: T) => void
function channel<T>() {
  const fns = new Set<Listener<T>>()
  return {
    on(fn: Listener<T>): () => void {
      fns.add(fn)
      return () => fns.delete(fn)
    },
    emit(e: T): void {
      for (const fn of [...fns]) {
        try {
          fn(e)
        } catch {
          /* one listener's bug never stops the others */
        }
      }
    },
  }
}

// ---- the page bus: stores/inbox (the socket) → /posts
const page = channel<SocialSignal>()
export const onSocialSignal = page.on
export const publishSocialSignal = page.emit

// ---- mock mode: MockPostsApi → api/realtime connectMock (= what the server would put on the socket)
const mock = channel<SocialChangedEvent>()
export const onMockSocial = mock.on
export function emitMockSocial(kind: SocialChangeKind, postIds: string[] | null = null): void {
  mock.emit({ type: 'social.changed', kind, post_ids: postIds })
}
